import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { MESSAGES, type MessageKey } from './messages'
import { formattersFor, type Formatters } from './format'
import { LANGS, pluralForm, type Lang, type Vars } from './types'

/**
 * Langue de l'interface.
 *
 * Pas de bibliothèque i18n : le besoin est de deux langues, sans pluriels
 * complexes ni chargement différé. Un contexte et un catalogue typé pèsent
 * quelques kilo-octets et donnent une garantie que `react-i18next` ne donne
 * pas — une clé absente ou une traduction manquante échoue à la compilation,
 * pas en production devant un client.
 *
 * Le fournisseur est monté au-dessus de l'authentification : le sélecteur est
 * donc utilisable sur l'écran de connexion comme dans l'application.
 */

const STORAGE_KEY = 'icc_lang'

function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as string[]).includes(value)
}

/** Français par défaut. La langue du navigateur ne décide pas : c'est un choix explicite. */
export function readLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLang(stored) ? stored : 'fr'
  } catch {
    // Navigation privée ou stockage refusé : la langue vaut pour la session.
    return 'fr'
  }
}

function writeLang(lang: Lang) {
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    /* stockage indisponible : le choix vaut pour la session seulement */
  }
}

/**
 * Langue courante hors React.
 *
 * Quelques fonctions utilitaires (traduction des erreurs réseau, par exemple)
 * sont appelées en dehors du rendu et ne peuvent pas lire un contexte. Ce
 * miroir leur donne la langue sans la faire traverser toutes les signatures.
 */
let currentLang: Lang = readLang()

export function activeLang(): Lang {
  return currentLang
}

function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}

/**
 * Rend un message.
 *
 * Un message au pluriel choisit sa forme sur `n` ; sans `n`, c'est le
 * singulier, le seul choix qui ne suppose rien.
 */
function render(key: MessageKey, lang: Lang, vars?: Vars): string {
  const entry = MESSAGES[key][lang]
  if (typeof entry === 'string') return interpolate(entry, vars)
  const n = typeof vars?.n === 'number' ? vars.n : Number(vars?.n ?? 1)
  return interpolate(entry[pluralForm(lang, Number.isFinite(n) ? n : 1)], vars)
}

/** Traduction hors composant. Dans un composant, préférer `useT()`, qui réagit au changement. */
export function translate(key: MessageKey, vars?: Vars): string {
  return render(key, currentLang, vars)
}

export type TFunction = (key: MessageKey, vars?: Vars) => string

interface I18nState {
  lang: Lang
  setLang: (lang: Lang) => void
  t: TFunction
  fmt: Formatters
}

const I18nContext = createContext<I18nState | null>(null)

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readLang)

  const setLang = useCallback((next: Lang) => {
    currentLang = next
    setLangState(next)
    writeLang(next)
  }, [])

  // L'attribut lang de la page : lecteurs d'écran, césure et correcteur
  // orthographique des zones de saisie s'y réfèrent.
  useEffect(() => {
    currentLang = lang
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo<I18nState>(
    () => ({
      lang,
      setLang,
      t: (key, vars) => render(key, lang, vars),
      fmt: formattersFor(lang),
    }),
    [lang, setLang],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nState {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n doit être utilisé dans I18nProvider')
  return ctx
}

/** Raccourci pour le cas courant : seule la fonction de traduction est utile. */
export function useT(): TFunction {
  return useI18n().t
}

/** Raccourci pour les écrans qui ne formatent que des nombres et des dates. */
export function useFormat(): Formatters {
  return useI18n().fmt
}
